import {
  CAMPAIGN_STATUSES,
  type CampaignFilters,
  type CampaignStatusKey,
  campaignCouponSuggestion,
  campaignIsManual,
  campaignStatusLabel,
  toCampaignFilters,
} from '@crm/shared';
import { Button, Label, Modal, Segmented, Select, Spinner, Steps } from '@crm/ui';
import {
  Archive,
  ArrowLeft,
  ChartNoAxesColumn,
  Check,
  ChevronLeft,
  ChevronRight,
  Circle,
  type LucideIcon,
  Megaphone,
  MessageSquare,
  Pause,
  PencilLine,
  Play,
  Send,
  Target,
  Trash2,
  Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ConfirmDialog, PageHeader } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { useCan } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { useUnits } from '@/modules/company/hooks';
import { useCoupons, useCreateCoupon, useUpdateCoupon } from '@/modules/coupons/api';
import {
  CAMPAIGNS_ROUTES,
  type CampaignInput,
  useCampaign,
  useCampaignPreview,
  useDeleteCampaign,
  useRunCampaign,
  useUpdateCampaign,
} from '../api';
import { AudienceTab } from '../components/audience-tab';
import { type CouponDraft, CouponField } from '../components/coupon-field';
import { MessageTab } from '../components/message-tab';
import { ObjectiveTab } from '../components/objective-tab';
import { PendingNotice } from '../components/pending-notice';
import { ResultsTab } from '../components/results-tab';
import { checklistDaCampanha, pendenciasDaCampanha } from '../readiness';

/** Cada situacao e cada frequencia com o seu simbolo, para ler sem soletrar. */
const ICONE_DA_SITUACAO: Record<CampaignStatusKey, LucideIcon> = {
  DRAFT: PencilLine,
  ACTIVE: Play,
  PAUSED: Pause,
  ARCHIVED: Archive,
};

const ABAS = [
  { key: 'objetivo', label: 'Objetivo', icon: Target },
  { key: 'publico', label: 'Publico', icon: Users },
  { key: 'mensagem', label: 'Mensagem', icon: MessageSquare },
  { key: 'resultados', label: 'Resultados', icon: ChartNoAxesColumn },
] as const;

type Aba = (typeof ABAS)[number]['key'];

export function CampaignPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const canWrite = useCan('customers', 'WRITE');

  const [aba, setAba] = useState<Aba>('objetivo');
  const [rascunho, setRascunho] = useState<CampaignInput | null>(null);
  const [confirmandoDisparo, setConfirmandoDisparo] = useState(false);
  // O que falta so aparece na hora de disparar ou ativar: fora disso era um
  // aviso permanente sobre uma campanha que ainda estava sendo montada.
  const [vendoChecklist, setVendoChecklist] = useState<'disparo' | 'ativacao' | null>(null);
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);

  const campaign = useCampaign(id);
  const preview = useCampaignPreview(rascunho?.filters, rascunho?.objective ?? 'COUPON');
  // So percentual: "R$ 20 de desconto" nao cabe numa oferta que a pessoa
  // ainda nao sabe quanto vai custar.
  const cupons = useCoupons({ page: 1, perPage: 50, status: 'active' });
  const _createCoupon = useCreateCoupon();
  const updateCoupon = useUpdateCoupon();
  const [cupom, setCupom] = useState<CouponDraft | null>(null);
  const _units = useUnits();
  const updateCampaign = useUpdateCampaign(id);
  const deleteCampaign = useDeleteCampaign();
  const runCampaign = useRunCampaign(id);

  useDocumentTitle(campaign.data?.name ?? 'Campanha');

  useEffect(() => {
    if (!campaign.data) return;

    setRascunho({
      status: campaign.data.status,
      objective: campaign.data.objective,
      specialDate: campaign.data.specialDate,
      scheduledAt: campaign.data.scheduledAt,
      body: campaign.data.body,
      templateId: campaign.data.templateId,
      couponId: campaign.data.couponId,
      sendWindowStart: campaign.data.sendWindowStart,
      sendWindowEnd: campaign.data.sendWindowEnd,
      filters: campaign.data.filters,
    });
  }, [campaign.data]);

  if (campaign.isPending || !rascunho) {
    return (
      <div className="flex justify-center py-20">
        <Spinner />
      </div>
    );
  }

  function altera(valores: CampaignInput) {
    setRascunho((atual) => (atual ? { ...atual, ...valores } : atual));
  }

  /**
   * Acerta o cupom preso, se alguem mexeu no codigo ou no percentual, e
   * devolve o id para gravar junto da campanha.
   *
   * Criar e escolher acontecem na janela, na hora - aqui so sobra o ajuste,
   * que anda junto do resto da tela em vez de pedir um segundo clique.
   */
  async function garanteCupom(): Promise<string | null> {
    if (!cupomPreso) return rascunho?.couponId ?? null;

    const code = cupomAtual.code.trim();
    const value = Number(cupomAtual.percent);

    if (!code || !Number.isFinite(value) || value <= 0) return cupomPreso.id;
    if (cupomPreso.code === code && cupomPreso.value === value) return cupomPreso.id;

    const trocado = await updateCoupon.mutateAsync({ id: cupomPreso.id, input: { code, value } });

    return trocado.id;
  }

  async function grava(): Promise<void> {
    if (!rascunho) return;

    const couponId = await garanteCupom();

    await updateCampaign.mutateAsync({ ...rascunho, couponId });
    setCupom(null);
  }

  /** Ativar passa pelo checklist; as demais situacoes gravam direto. */
  function trocaSituacao(status: CampaignStatusKey) {
    if (status === 'ACTIVE' && faltaParaAtivar) {
      setVendoChecklist('ativacao');
      return;
    }

    altera({ status });
  }

  function salva() {
    void grava();
  }

  /** Trocar de passo grava o que estava na tela - inclusive andando para tras. */
  function vaiPara(passo: number) {
    const destino = ABAS[Math.min(Math.max(passo, 0), ABAS.length - 1)];
    if (!destino) return;

    if (canWrite && alterado) void grava();
    setAba(destino.key);
  }

  const objetivo = rascunho.objective ?? 'COUPON';
  const manual = campaignIsManual(objetivo);

  // Sem cupom escolhido, os campos ja vem preenchidos pelo objetivo: `cupom`
  // nulo quer dizer "ninguem mexeu", e nao "vazio".
  const sugestaoDeCupom = campaignCouponSuggestion(objetivo, rascunho.specialDate ?? null);
  const cupomPreso = cupons.data?.data.find((item) => item.id === rascunho.couponId);
  const cupomBase: CouponDraft = {
    code: cupomPreso?.code ?? sugestaoDeCupom.code,
    percent: String(cupomPreso?.value ?? sugestaoDeCupom.percent),
  };
  const cupomAtual = cupom ?? cupomBase;
  const cupomAlterado =
    cupom !== null && (cupom.code !== cupomBase.code || cupom.percent !== cupomBase.percent);
  const erro = updateCampaign.error ?? runCampaign.error ?? deleteCampaign.error;
  const total = preview.data?.total ?? 0;
  const alterado =
    cupomAlterado ||
    JSON.stringify(rascunho) !==
      JSON.stringify(
        campaign.data
          ? {
              status: campaign.data.status,
              objective: campaign.data.objective,
              specialDate: campaign.data.specialDate,
              scheduledAt: campaign.data.scheduledAt,
              body: campaign.data.body,
              templateId: campaign.data.templateId,
              couponId: campaign.data.couponId,
              sendWindowStart: campaign.data.sendWindowStart,
              sendWindowEnd: campaign.data.sendWindowEnd,
              filters: campaign.data.filters,
            }
          : null,
      );

  const indice = ABAS.findIndex((item) => item.key === aba);
  const checklist = checklistDaCampanha({
    name: campaign.data?.name ?? '',
    body: rascunho.body ?? '',
    total,
    ativa: rascunho.status === 'ACTIVE',
    alterado,
    modeloAprovado: campaign.data?.template?.approval === 'APPROVED',
  });
  const faltaAlgo = checklist.some((item) => !item.ok);
  // Ativar nao exige estar ativa - e o proprio ato -, nem estar gravado: a
  // gravacao vem junto.
  const faltaParaAtivar = checklist.some(
    (item) => !item.ok && item.key !== 'ativa' && item.key !== 'salvo',
  );
  const pendencias = pendenciasDaCampanha({
    name: campaign.data?.name ?? '',
    body: rascunho.body ?? '',
    total,
    ativa: rascunho.status === 'ACTIVE',
    alterado,
    modeloAprovado: campaign.data?.template?.approval === 'APPROVED',
  });

  const comLeads = (campaign.data?.leadsTotal ?? 0) > 0;

  return (
    // A altura minima empurra a navegacao para o fim da tela mesmo em passo
    // curto: sem isso a barra parava no meio da pagina, solta.
    <div className="flex min-h-[calc(100vh-8rem)] flex-col space-y-5">
      <div>
        <Link
          to={CAMPAIGNS_ROUTES.LIST}
          className="-ml-1 mb-1 inline-flex min-h-10 items-center gap-1.5 px-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Campanhas
        </Link>

        <PageHeader
          icon={<Megaphone />}
          title={campaign.data?.name ?? 'Campanha'}
          description={`${campaignStatusLabel(rascunho.status ?? 'DRAFT')} - ${preview.data?.total ?? 0} destinatarios - ${campaign.data?.dispatchLabel ?? ''}`}
          actions={
            canWrite ? (
              <>
                {/* Campanha que ja gerou lead nao se apaga: o funil dela
                    perderia a origem. Ali o caminho e arquivar. */}
                <Button
                  variant="ghost"
                  onClick={() => setConfirmandoExclusao(true)}
                  disabled={comLeads}
                  title={
                    comLeads ? 'Ja tem lead no funil - arquive em Dados, na situacao' : undefined
                  }
                >
                  <Trash2 className="h-4 w-4" />
                  Apagar
                </Button>
                <Button
                  onClick={() => setConfirmandoDisparo(true)}
                  title={pendencias[0]?.texto ?? 'Enviar agora para o publico escolhido'}
                  disabled={runCampaign.isPending || pendencias.length > 0}
                >
                  {runCampaign.isPending ? <Spinner /> : <Send className="h-4 w-4" />}
                  <span>Disparar</span>
                </Button>
              </>
            ) : null
          }
        />
      </div>

      {erro ? <p className="text-sm text-destructive">{toErrorMessage(erro)}</p> : null}

      <Steps
        passos={ABAS}
        atual={indice}
        onSelect={(passo) => vaiPara(passo)}
        temPendencia={(passo) => pendencias.some((item) => item.passo === passo)}
      />

      {aba === 'objetivo' ? (
        <ObjectiveTab
          objective={objetivo}
          specialDate={rascunho.specialDate ?? null}
          scheduledAt={rascunho.scheduledAt ?? null}
          filters={toCampaignFilters(rascunho.filters)}
          readOnly={!canWrite}
          onChange={altera}
        >
          <CouponField
            attached={cupomPreso}
            available={(cupons.data?.data ?? []).filter((item) => item.type === 'PERCENT')}
            draft={cupomAtual}
            onDraftChange={setCupom}
            onPick={(couponId) => {
              setCupom(null);
              altera({ couponId });
            }}
            suggestion={{
              code: sugestaoDeCupom.code,
              percent: String(sugestaoDeCupom.percent),
            }}
            readOnly={!canWrite}
          />
        </ObjectiveTab>
      ) : null}

      {aba === 'publico' ? (
        <AudienceTab
          filters={toCampaignFilters(rascunho.filters) as CampaignFilters}
          objective={objetivo}
          readOnly={!canWrite}
          onChange={(filters) => altera({ filters })}
          dispatching={runCampaign.isPending}
          {...(manual && canWrite
            ? {
                onDispatch: () =>
                  faltaAlgo ? setVendoChecklist('disparo') : setConfirmandoDisparo(true),
              }
            : {})}
        />
      ) : null}

      {aba === 'mensagem' ? (
        <MessageTab
          campaignId={id}
          body={rascunho.body ?? ''}
          couponId={rascunho.couponId ?? null}
          templateId={rascunho.templateId ?? null}
          campaignName={campaign.data?.name ?? ''}
          sendWindowStart={rascunho.sendWindowStart ?? null}
          sendWindowEnd={rascunho.sendWindowEnd ?? null}
          readOnly={!canWrite}
          onChange={altera}
        />
      ) : null}

      {/* Sem disparo nao ha numero: no lugar da tela vazia, o que falta. */}
      {aba === 'resultados' ? (
        pendencias.length > 0 ? (
          <PendingNotice
            pendencias={pendencias}
            nomeDoPasso={(passo) => ABAS[passo]?.label ?? ''}
            onIr={vaiPara}
          />
        ) : (
          <ResultsTab campaignId={id} />
        )
      ) : null}

      {/* A navegacao fica presa no rodape: no celular, um botao no fim da
          pagina obriga a rolar a tela toda de volta. */}
      <div className="sticky bottom-0 -mx-4 mt-auto border-t border-border bg-background/95 px-4 py-3 backdrop-blur sm:mx-0 sm:rounded-card sm:border sm:px-4">
        {/* A situacao vale para a campanha inteira, nao para o passo aberto -
            por isso mora aqui, a vista em todos eles. Em linha propria e
            separada por um traco: encostada nos botoes, parecia mais uma
            escolha de navegacao.

            Sem as dicas de cada situacao, que numa barra fixa empurrariam o
            conteudo da pagina para cima em todos os passos. */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border pb-3">
          <Label className="font-semibold" htmlFor="campanha-situacao">
            Situacao:
          </Label>

          {/* No celular as quatro opcoes quebravam em duas linhas e a barra
              fixa comia um quarto da tela. A lista diz o mesmo em uma linha;
              da largura de tablet para cima, as opcoes voltam a vista. */}
          <Select
            id="campanha-situacao"
            className="w-40 sm:hidden"
            value={rascunho.status ?? 'DRAFT'}
            disabled={!canWrite}
            onChange={(evento) => trocaSituacao(evento.target.value as CampaignStatusKey)}
          >
            {CAMPAIGN_STATUSES.map((item) => (
              <option key={item.key} value={item.key}>
                {item.label}
              </option>
            ))}
          </Select>

          <Segmented
            flat
            label="Situacao da campanha"
            className="hidden sm:block"
            options={CAMPAIGN_STATUSES.map((item) => ({
              value: item.key,
              label: item.label,
              icon: ICONE_DA_SITUACAO[item.key],
            }))}
            value={rascunho.status ?? 'DRAFT'}
            disabled={!canWrite}
            onChange={trocaSituacao}
          />
        </div>

        <div className="flex items-center justify-between gap-2 pt-3">
          <Button variant="ghost" disabled={indice === 0} onClick={() => vaiPara(indice - 1)}>
            <ChevronLeft className="h-4 w-4" />
            Anterior
          </Button>

          <div className="flex items-center gap-2">
            {canWrite && alterado ? (
              <Button
                variant="ghost"
                onClick={() => campaign.data && setRascunho({ ...campaign.data })}
              >
                Descartar
              </Button>
            ) : null}

            {indice < ABAS.length - 1 ? (
              // Avancar grava o que foi feito: o passo seguinte so faz sentido
              // sobre o anterior ja salvo, e ninguem precisa lembrar de salvar.
              <Button onClick={() => vaiPara(indice + 1)} disabled={updateCampaign.isPending}>
                {updateCampaign.isPending ? <Spinner /> : null}
                {canWrite && alterado ? (
                  <span>
                    Salvar<span className="hidden sm:inline"> e continuar</span>
                  </span>
                ) : (
                  'Proximo'
                )}
                <ChevronRight className="h-4 w-4" />
              </Button>
            ) : canWrite && alterado ? (
              <Button onClick={salva} disabled={updateCampaign.isPending}>
                {updateCampaign.isPending ? <Spinner /> : null}
                Salvar
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      {/* O checklist so aparece quando a pessoa pede a acao: e ali que ele
          responde a pergunta que ela acabou de fazer. */}
      {vendoChecklist ? (
        <Modal
          open
          size="small"
          title={
            vendoChecklist === 'disparo' ? 'Ainda nao da para disparar' : 'Ainda nao da para ativar'
          }
          onClose={() => setVendoChecklist(null)}
          actions={[{ label: 'Entendi', onClick: () => setVendoChecklist(null) }]}
        >
          <ul className="space-y-2">
            {checklist
              .filter(
                (item) =>
                  vendoChecklist === 'disparo' || (item.key !== 'ativa' && item.key !== 'salvo'),
              )
              .map((item) => (
                <li key={item.key} className="flex items-center gap-2 text-sm">
                  {item.ok ? (
                    <Check className="h-4 w-4 shrink-0 text-primary" />
                  ) : (
                    <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                  <span className={item.ok ? 'text-muted-foreground line-through' : ''}>
                    {item.label}
                  </span>
                </li>
              ))}
          </ul>
        </Modal>
      ) : null}

      <ConfirmDialog
        open={confirmandoDisparo}
        title="Disparar a campanha?"
        message={
          <>
            <strong>{campaign.data?.name}</strong> vai enviar mensagem para{' '}
            <strong>{preview.data?.total ?? 0} pessoas</strong> agora.
            <span className="mt-2 block rounded-card bg-muted px-3 py-2 text-sm">
              {(rascunho.body ?? '').slice(0, 120)}
            </span>
          </>
        }
        confirmLabel="Disparar"
        confirmVariant="default"
        loading={runCampaign.isPending}
        onConfirm={() => {
          runCampaign.mutate(undefined, { onSuccess: () => setAba('resultados') });
          setConfirmandoDisparo(false);
        }}
        onClose={() => setConfirmandoDisparo(false)}
      />

      <ConfirmDialog
        open={confirmandoExclusao}
        title="Apagar a campanha?"
        message="O historico de disparos vai junto."
        confirmLabel="Apagar"
        loading={deleteCampaign.isPending}
        onConfirm={() => {
          deleteCampaign.mutate(id, { onSuccess: () => navigate(CAMPAIGNS_ROUTES.LIST) });
          setConfirmandoExclusao(false);
        }}
        onClose={() => setConfirmandoExclusao(false)}
      />
    </div>
  );
}
